import { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from './apiConfig';

/**
 * Custom hook to fetch master data dropdown options by type key
 * @param {string} typeKey - The TypeKey from mastertype table
 * @param {boolean} includeInactive - Include inactive values (default: false)
 * @returns {Array} Array of options in format { value: MasterValueId, label: DisplayName }
 */
export const useMasterData = (typeKey, includeInactive = false) => {
  const [options, setOptions] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!typeKey) {
      setOptions([]);
      return;
    }

    const fetchMasterData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${getApiBaseUrl()}/master/values/bykey/${typeKey}`, {
          params: { includeInactive }
        });
        if (response.status === 200 && response.data?.success) {
          const data = response.data.data || [];
          const mappedOptions = data.map(item => ({
            value: item.MasterValueId.toString(),
            label: item.DisplayName,
            name: typeKey
          }));
          setItems(data);
          setOptions(mappedOptions);
        }
      } catch (err) {
        console.error(`Failed to fetch master data for ${typeKey}:`, err);
        setError(err);
        setItems([]);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMasterData();
  }, [typeKey, includeInactive]);

  return { options, items, loading, error };
};

/**
 * Custom hook to fetch multiple master data types at once
 * @param {Array<string>} typeKeys - Array of TypeKey strings
 * @param {boolean} includeInactive - Include inactive values (default: false)
 * @returns {Object} Object with typeKey as keys and options arrays as values
 */
export const useMasterDataBatch = (typeKeys = [], includeInactive = false) => {
  const [dataMap, setDataMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!typeKeys || typeKeys.length === 0) {
      setDataMap({});
      return;
    }

    const fetchBatchMasterData = async () => {
      setLoading(true);
      setError(null);
      try {
        const promises = typeKeys.map(typeKey =>
          axios.get(`${getApiBaseUrl()}/master/values/bykey/${typeKey}`, {
            params: { includeInactive }
          })
        );
        const responses = await Promise.all(promises);
        
        const newDataMap = {};
        responses.forEach((response, index) => {
          const typeKey = typeKeys[index];
          if (response.status === 200 && response.data?.success) {
            const data = response.data.data || [];
            newDataMap[typeKey] = data.map(item => ({
              value: item.MasterValueId.toString(),
              label: item.DisplayName,
              name: typeKey
            }));
          } else {
            newDataMap[typeKey] = [];
          }
        });
        setDataMap(newDataMap);
      } catch (err) {
        console.error('Failed to fetch batch master data:', err);
        setError(err);
        const emptyMap = {};
        typeKeys.forEach(key => { emptyMap[key] = []; });
        setDataMap(emptyMap);
      } finally {
        setLoading(false);
      }
    };

    fetchBatchMasterData();
  }, [JSON.stringify(typeKeys), includeInactive]);

  return { dataMap, loading, error };
};
